class AtcSummary extends HTMLElement {
  connectedCallback() {
    this.data = this.parseData();
    if (!this.data) return;

    this.formId = this.dataset.formId;
    this.form = this.formId ? document.getElementById(this.formId) : null;
    this.labels = {
      subscription: this.dataset.labelSubscription || 'subscription',
      onetime: this.dataset.labelOnetime || 'one-time',
      assorted: this.dataset.labelAssorted || 'Assorted',
      countSuffix: this.dataset.countSuffix || '-count',
    };

    const scope = this.form || document;
    this.priceEl = scope.querySelector('[data-atc-price]');
    this.subtitleEl = scope.querySelector('[data-atc-subtitle]');
    // SKIO writes its computed price (full one-time / discounted subscription)
    // into this hidden [data-product-price] sink via its external_price_selector.
    this.skioPriceEl = this.querySelector('[data-atc-skio-price]');
    this.optionSelectors = Array.from(document.querySelectorAll('[data-single-option-selector]'));
    // Pack selector buttons live alongside this element in the same block; their
    // prices swap between the product's one-time price and the subscription price
    // whenever the one-time / subscription choice changes.
    this.packCards = this.parentElement
      ? Array.from(this.parentElement.querySelectorAll('.pack-card'))
      : [];

    this.refresh = this.refresh.bind(this);
    this.applyPrice = this.applyPrice.bind(this);

    // SKIO bubbles this whenever the one-time / subscription choice or frequency
    // changes. Refresh on the next frame so our price wins over Alpine's own
    // reactive write to the same element in the current tick.
    document.addEventListener('skio::update-selling-plan', () => requestAnimationFrame(this.refresh));
    this.optionSelectors.forEach((el) =>
      el.addEventListener('change', () => requestAnimationFrame(this.refresh))
    );

    // The quantity stepper changes the value through Alpine's x-model, which
    // does not fire an input event, and Alpine then writes the quantity-
    // multiplied total back into the price element. Watch the element and
    // re-assert our per-unit price whenever that happens.
    if (this.priceEl) {
      this.priceObserver = new MutationObserver(this.applyPrice);
      this.priceObserver.observe(this.priceEl, {
        childList: true,
        characterData: true,
        subtree: true,
      });
    }

    // Re-read the price whenever SKIO updates its computed value.
    if (this.skioPriceEl) {
      this.skioObserver = new MutationObserver(() => requestAnimationFrame(this.refresh));
      this.skioObserver.observe(this.skioPriceEl, {
        childList: true,
        characterData: true,
        subtree: true,
      });
    }

    this.refresh();
  }

  parseData() {
    const el = this.querySelector('[data-atc-summary-data]');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  getSelectedOptionValues() {
    const values = [];
    this.optionSelectors.forEach((el) => {
      if (el.tagName === 'SELECT') {
        values.push(el.value);
      } else if (el.checked) {
        values.push(el.value);
      }
    });
    return values;
  }

  resolveVariant() {
    const variants = this.data.variants || [];
    if (variants.length <= 1) return variants[0] || null;
    const selected = this.getSelectedOptionValues();
    if (selected.length) {
      const match = variants.find((v) => selected.every((val) => v.options.includes(val)));
      if (match) return match;
    }
    return variants.find((v) => v.available) || variants[0] || null;
  }

  getSellingPlanInput() {
    // SKIO's input lives inside <skio-plan-picker>, linked to the form via its
    // form attribute, so it's in form.elements but not found by querySelector.
    // namedItem returns a RadioNodeList when several controls share the name.
    if (!this.form || !this.form.elements) return null;
    let input = this.form.elements.namedItem('selling_plan');
    if (input && typeof input.item === 'function') input = input.item(0);
    return input || null;
  }

  getSelectedPlanId() {
    // SKIO writes the selected Shopify selling plan id here; empty for one-time.
    const input = this.getSellingPlanInput();
    const id = input && input.value ? Number(input.value) : 0;
    return Number.isFinite(id) ? id : 0;
  }

  isSubscriptionSelected() {
    return this.getSelectedPlanId() > 0;
  }

  parsePriceToCents(value) {
    if (value == null) return null;
    const cleaned = String(value).replace(/[^0-9.]/g, '');
    if (!cleaned) return null;
    const amount = parseFloat(cleaned);
    if (!Number.isFinite(amount)) return null;
    return Math.round(amount * 100);
  }

  getFlavour(variant) {
    // Only a product whose pack reveals the variant picker carries a real
    // flavour; the fixed packs (starter / variety) are assorted.
    if (this.data.revealsVariantPicker && variant) {
      const value = (variant.options && variant.options[0]) || variant.title;
      if (value && value !== 'Default Title') return value;
    }
    return this.labels.assorted;
  }

  refresh() {
    const variant = this.resolveVariant();
    const subscribed = this.isSubscriptionSelected();

    // Prefer SKIO's own computed per-unit price (it applies the subscription
    // discount that Shopify's variant price does not carry). Fall back to the
    // manually entered subscription price, then the product/variant price.
    const skioText = this.skioPriceEl ? this.skioPriceEl.textContent.trim() : '';
    if (skioText) {
      this.desiredPriceText = skioText;
    } else {
      let unitPrice;
      if (subscribed) {
        const manualCents = this.parsePriceToCents(this.data.subscriptionPrice);
        unitPrice = manualCents != null ? manualCents : variant ? variant.price : this.data.price;
      } else {
        unitPrice = variant ? variant.price : this.data.price;
      }
      this.desiredPriceText = this.formatMoney(unitPrice);
    }

    // Show the per-unit price (not multiplied by quantity). applyPrice re-asserts
    // it over the theme's Alpine total whenever the quantity changes.
    this.applyPrice();

    this.updatePackCards(subscribed);
    this.updatePackCardFlavour();

    if (this.subtitleEl) {
      const parts = [];
      const flavour = this.getFlavour(variant);
      if (flavour) parts.push(flavour);
      if (this.data.count) parts.push(`${this.data.count}${this.labels.countSuffix}`);
      parts.push(subscribed ? this.labels.subscription : this.labels.onetime);
      const summary = parts.join(' · ');
      requestAnimationFrame(() => {
        this.subtitleEl.textContent = summary;
      });
    }
  }

  applyPrice() {
    // Only write when the text differs, so re-asserting our value over Alpine's
    // total does not loop against our own MutationObserver.
    if (!this.priceEl || this.desiredPriceText == null) return;
    if (this.priceEl.textContent !== this.desiredPriceText) {
      this.priceEl.textContent = this.desiredPriceText;
    }
  }

  updatePackCards(subscribed) {
    if (!this.packCards || !this.packCards.length) return;
    this.packCards.forEach((card) => {
      const priceEl = card.querySelector('[data-pack-card-price]');
      if (!priceEl) return;
      const onetimeMoney = priceEl.dataset.onetimeMoney;
      let money = onetimeMoney;
      if (subscribed) {
        // The active card reflects the live per-unit price (which carries any
        // Skio subscription discount); the others fall back to their configured
        // subscription price, then to the one-time price.
        if (card.classList.contains('is-active') && this.desiredPriceText) {
          money = this.desiredPriceText;
        } else {
          const cents = this.parsePriceToCents(priceEl.dataset.subscriptionPrice);
          money = cents != null ? this.formatMoney(cents) : onetimeMoney;
        }
      }
      const markup = this.renderPackCardPrice(money);
      if (priceEl.innerHTML !== markup) priceEl.innerHTML = markup;
    });
  }

  getFlavourHandle() {
    const input = this.optionSelectors.find(
      (el) => el.checked && el.dataset.position === '1'
    );
    return input && input.dataset.handle ? input.dataset.handle : null;
  }

  updatePackCardFlavour() {
    if (!this.packCards || !this.packCards.length) return;
    const active = this.packCards.find((card) => card.classList.contains('is-active'));
    if (!active) return;

    const handle = this.data.revealsVariantPicker ? this.getFlavourHandle() : null;
    if (handle) {
      active.dataset.flavour = handle;
    } else {
      delete active.dataset.flavour;
    }
  }

  renderPackCardPrice(money) {
    if (money == null) return '';
    const parts = String(money).split('.');
    if (parts.length > 1) {
      const cents = parts.slice(1).join('.');
      return `<span class="pack-card__price-whole">${parts[0]}</span><span class="pack-card__price-cents">.${cents}</span>`;
    }
    return money;
  }

  formatMoney(cents) {
    const format = window.theme && window.theme.moneyFormat;
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents, format);
    }
    return '$' + (cents / 100).toFixed(2);
  }
}

if (!customElements.get('atc-summary')) {
  customElements.define('atc-summary', AtcSummary);
}
